import { itx } from "../../../src/translationItx/itx";
import type { ReactNode } from "react";
import remarkDirective from "remark-directive";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";

type MarkdownNode = {
  type: string;
  value?: string;
  depth?: number;
  ordered?: boolean;
  start?: number | null;
  checked?: boolean | null;
  url?: string;
  title?: string | null;
  alt?: string;
  identifier?: string;
  name?: string;
  attributes?: Record<string, string> | null;
  lang?: string | null;
  children?: MarkdownNode[];
  align?: Array<"left" | "right" | "center" | null>;
};

export type C1_MarkdownPdfPrimitives = {
  View: any;
  Text: any;
  Link: any;
  Image: any;
};

type Props = {
  markdown: string;
  primitives: C1_MarkdownPdfPrimitives;
  logo: string;
  textAlign?: "left" | "center";
};

const POINTS_PER_CM = 72 / 2.54;

function lengthInPoints(value?: string): number | undefined {
  if (!value) return undefined;
  const match = /^(-?\d+(?:\.\d+)?)(cm|mm|pt|px)?$/i.exec(value.trim());
  if (!match) return undefined;
  const amount = Number(match[1]);
  switch (match[2]?.toLowerCase()) {
    case "cm": return amount * POINTS_PER_CM;
    case "mm": return (amount * POINTS_PER_CM) / 10;
    case "px": return amount * 0.75;
    default: return amount;
  }
}

export function C1_MarkdownPdfRenderer({ markdown, primitives, logo, textAlign = "left" }: Props) {
  const { View, Text, Link, Image } = primitives;
  const tree = unified().use(remarkParse).use(remarkGfm).use(remarkDirective).parse(markdown) as MarkdownNode;
  const definitions = new Map<string, MarkdownNode>();

  const collectDefinitions = (node: MarkdownNode) => {
    if (node.type === "definition" && node.identifier) definitions.set(node.identifier.toLowerCase(), node);
    node.children?.forEach(collectDefinitions);
  };
  collectDefinitions(tree);

  const inlineText = (nodes: MarkdownNode[] | undefined, keyPrefix: string): ReactNode[] =>
    (nodes ?? []).map((node, index) => renderInline(node, `${keyPrefix}-${index}`));

  const renderInline = (node: MarkdownNode, key: string): ReactNode => {
    switch (node.type) {
      case "text": return node.value ?? "";
      case "strong": return <Text key={key} style={{ fontWeight: 700 }}>{inlineText(node.children, key)}</Text>;
      case "emphasis": return <Text key={key} style={{ fontStyle: "italic" }}>{inlineText(node.children, key)}</Text>;
      case "delete": return <Text key={key} style={{ textDecoration: "line-through" }}>{inlineText(node.children, key)}</Text>;
      case "inlineCode":
        return <Text key={key} style={{ fontFamily: "Courier", backgroundColor: "#f2f2f2" }}>{node.value ?? ""}</Text>;
      case "break": return "\n";
      case "link":
        return <Link key={key} src={node.url ?? ""} style={{ color: "#0b66c3", textDecoration: "underline" }}>{inlineText(node.children, key)}</Link>;
      case "linkReference": {
        const definition = definitions.get((node.identifier ?? "").toLowerCase());
        return <Link key={key} src={definition?.url ?? ""} style={{ color: "#0b66c3", textDecoration: "underline" }}>{inlineText(node.children, key)}</Link>;
      }
      case "image": return node.alt ? `[${node.alt}]` : "[Bild]";
      case "imageReference": return node.alt ? `[${node.alt}]` : "[Bild]";
      case "footnoteReference": return <Text key={key} style={{ fontSize: 7, verticalAlign: "super" }}>{`[${node.identifier ?? ""}]`}</Text>;
      default: return inlineText(node.children, key);
    }
  };

  const renderDirective = (node: MarkdownNode, key: string): ReactNode => {
    const attributes = node.attributes ?? {};
    if (node.name === "separator") {
      const unknown = Object.keys(attributes).find((name) => !["height", "width"].includes(name));
      if (unknown) return renderError(`Unbekannter Parameter: "${unknown}"`, key);
      if (!attributes.height) return renderError("Fehlender Pflichtparameter: \"height\"", key);
      return <View key={key} style={{ height: lengthInPoints(attributes.height), width: lengthInPoints(attributes.width) }} />;
    }
    if (node.name === "logo") {
      const unknown = Object.keys(attributes).find((name) => !["height", "width", "left", "top", "right", "bottom", "center"].includes(name));
      if (unknown) return renderError(`Unbekannter Parameter: "${unknown}"`, key);
      if (!attributes.height) return renderError("Fehlender Pflichtparameter: \"height\"", key);
      const height = lengthInPoints(attributes.height);
      const width = lengthInPoints(attributes.width);
      const absolutelyPositioned = attributes.left != null || attributes.top != null || attributes.right != null || attributes.bottom != null;
      return (
        <View key={key} style={{ height, width, position: absolutelyPositioned ? "absolute" : undefined, left: lengthInPoints(attributes.left), top: lengthInPoints(attributes.top), right: lengthInPoints(attributes.right), bottom: lengthInPoints(attributes.bottom), alignSelf: attributes.center === "true" ? "center" : undefined, alignItems: "center", justifyContent: "center" }}>
          <Image src={logo} style={{ height, width, objectFit: "contain" }} />
        </View>
      );
    }
    return renderError(`Unbekannter Blockname: "::${node.name ?? ""}"`, key);
  };

  const renderError = (message: string, key: string) => (
    <View key={key} style={{ marginBottom: 4, padding: 8, borderRadius: 4, backgroundColor: "#fef2f2" }}>
      <Text style={{ color: "#b91c1c", fontSize: 10 }}>{itx("c1.ui.Direktiven_Fehler_value1", "Direktiven-Fehler: {{value1}}", { "{{value1}}": String(message) })}</Text>
    </View>
  );

  const renderList = (node: MarkdownNode, key: string, depth = 0): ReactNode => {
    const items = (node.children ?? []).filter((child) => child.type === "listItem");
    const start = node.start ?? 1;
    return (
      <View key={key} style={{ width: "100%", marginTop: depth === 0 ? 2 : 3, marginBottom: depth === 0 ? 10 : 0 }}>
        {items.map((item, index) => {
          const blocks = item.children ?? [];
          const firstParagraphIndex = blocks.findIndex((child) => child.type === "paragraph");
          const firstParagraph = firstParagraphIndex >= 0 ? blocks[firstParagraphIndex] : undefined;
          const remaining = blocks.filter((_, blockIndex) => blockIndex !== firstParagraphIndex);
          const marker = node.ordered ? `${start + index}.` : "•";
          const taskPrefix = item.checked == null ? "" : item.checked ? "[x] " : "[ ] ";
          return (
            <View key={`${key}-${index}`} wrap={false} style={{ width: "100%", marginBottom: 5 }}>
              <View style={{ width: "100%", flexDirection: "row", alignItems: "flex-start" }}>
                <Text style={{ width: node.ordered ? 22 : 16, fontSize: 10, lineHeight: 1.5 }}>{marker}</Text>
                <Text style={{ flexGrow: 1, flexBasis: 0, fontSize: 10, lineHeight: 1.5, textAlign }}>
                  {taskPrefix}{inlineText(firstParagraph?.children, `${key}-${index}-label`)}
                </Text>
              </View>
              {remaining.length > 0 && (
                <View style={{ marginLeft: node.ordered ? 22 : 16 }}>
                  {remaining.map((child, childIndex) => renderBlock(child, `${key}-${index}-${childIndex}`, depth + 1))}
                </View>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  const renderTable = (node: MarkdownNode, key: string) => {
    const rows = (node.children ?? []).filter((child) => child.type === "tableRow");
    const columnCount = Math.max(1, ...rows.map((row) => row.children?.length ?? 0));
    return (
      <View key={key} style={{ width: "100%", marginBottom: 12, borderTopWidth: 1, borderLeftWidth: 1, borderColor: "#b8c1c7" }}>
        {rows.map((row, rowIndex) => (
          <View key={`${key}-${rowIndex}`} wrap={false} style={{ flexDirection: "row", backgroundColor: rowIndex === 0 ? "#eef2f4" : "#fff" }}>
            {(row.children ?? []).map((cell, cellIndex) => (
              <Text key={`${key}-${rowIndex}-${cellIndex}`} style={{ width: `${100 / columnCount}%`, padding: 5, borderRightWidth: 1, borderBottomWidth: 1, borderColor: "#b8c1c7", fontSize: 8, fontWeight: rowIndex === 0 ? 700 : 400, textAlign: node.align?.[cellIndex] ?? "left" }}>
                {inlineText(cell.children, `${key}-${rowIndex}-${cellIndex}`)}
              </Text>
            ))}
          </View>
        ))}
      </View>
    );
  };

  const renderBlock = (node: MarkdownNode, key: string, depth = 0): ReactNode => {
    switch (node.type) {
      case "root": return <>{(node.children ?? []).map((child, index) => renderBlock(child, `${key}-${index}`, depth))}</>;
      case "paragraph": {
        const onlyChild = node.children?.length === 1 ? node.children[0] : undefined;
        if (onlyChild?.type === "image" && onlyChild.url) {
          return <Image key={key} src={onlyChild.url} style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain", marginBottom: 12 }} />;
        }
        return <Text key={key} style={{ marginBottom: 12, fontSize: 10, lineHeight: 1.5, textAlign }}>{inlineText(node.children, key)}</Text>;
      }
      case "heading": {
        const sizes: Record<number, number> = { 1: 30, 2: 20, 3: 15, 4: 12, 5: 11, 6: 10 };
        return <Text key={key} style={{ marginBottom: node.depth === 1 ? 10 : 7, fontSize: sizes[node.depth ?? 6], fontWeight: 700, lineHeight: 1.3, textAlign }}>{inlineText(node.children, key)}</Text>;
      }
      case "list": return renderList(node, key, depth);
      case "blockquote":
        return <View key={key} style={{ marginBottom: 12, paddingLeft: 10, borderLeftWidth: 3, borderLeftColor: "#b8c1c7" }}>{(node.children ?? []).map((child, index) => renderBlock(child, `${key}-${index}`, depth))}</View>;
      case "code":
        return <Text key={key} style={{ marginBottom: 12, padding: 8, fontFamily: "Courier", fontSize: 8.5, lineHeight: 1.35, backgroundColor: "#f4f6f7" }}>{node.value ?? ""}</Text>;
      case "thematicBreak": return <View key={key} style={{ marginVertical: 10, borderTopWidth: 1, borderTopColor: "#aeb9bf" }} />;
      case "table": return renderTable(node, key);
      case "leafDirective":
      case "containerDirective": return renderDirective(node, key);
      case "footnoteDefinition":
        return <View key={key} style={{ marginTop: 5, paddingTop: 5, borderTopWidth: 1, borderTopColor: "#d9e0e4" }}><Text style={{ fontSize: 7, fontWeight: 700 }}>{`[${node.identifier ?? ""}]`}</Text>{(node.children ?? []).map((child, index) => renderBlock(child, `${key}-${index}`, depth))}</View>;
      case "html": return null;
      case "definition": return null;
      default: return node.children ? <>{node.children.map((child, index) => renderBlock(child, `${key}-${index}`, depth))}</> : null;
    }
  };

  return <>{renderBlock(tree, "markdown")}</>;
}
